import { useQueryClient } from '@tanstack/react-query';
import { Application } from '../types/dbTypes';

/**
 * Canonical React Query key for a customer's current application. Centralized here so the
 * key isn't hardcoded across the dashboard query and the onboarding modals.
 */
export const applicationQueryKey = (userId?: string) => ['application', userId];

/**
 * Returns a setter that seeds the cached application outright, for a caller that already holds a
 * fresh copy of it — the response to its own write, say — that the dashboard is about to go and
 * ask the server for.
 *
 * The point is the render BEFORE that request comes back. The dashboard draws itself whether or
 * not it has an application yet, but its onboarding modals can't, so a customer handed to the
 * dashboard to be shown one sits looking at the dashboard until the round trip lands. Seeding the
 * cache means the first render already has it; the query's staleTime keeps it from immediately
 * refetching over the top.
 */
export function usePrimeApplication(userId?: string) {
  const queryClient = useQueryClient();

  return (application: Application) => {
    queryClient.setQueryData<Application>(
      applicationQueryKey(userId),
      application
    );
  };
}

/**
 * Returns a setter that optimistically writes a new `application_step` into the cached
 * application, so the dashboard reflects a step change immediately instead of flashing the
 * stale card while a background refetch is in flight. Callers should still invalidate/refetch
 * afterwards to reconcile with the server.
 */
export function useOptimisticApplicationStep(userId?: string) {
  const queryClient = useQueryClient();

  return (step: string) => {
    const queryKey = applicationQueryKey(userId);
    // Cancel any in-flight refetch so its (older) response can't clobber this optimistic write.
    void queryClient.cancelQueries({ queryKey });
    queryClient.setQueryData<Application>(queryKey, (old) =>
      old
        ? { ...old, application_step: step as Application['application_step'] }
        : old
    );
  };
}
